import { connect, ConnectedProps } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom-v5-compat';

import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import { Backdrop } from 'dashboard/layouts/ShowViewLayoutV2';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { productFeatureDisabled } from 'dashboard/lib/productFeature';
import { ExternalIntegration } from 'dashboard/models/external-integration';
import { ProductFeatureName } from 'dashboard/models/organization';
import { clearExternalIntegrations } from 'dashboard/redux/actions/external-integrations';
import { showMessage } from 'dashboard/redux/actions/messages';
import { RootState } from 'dashboard/redux/store';
import {
  archiveExternalIntegration,
  createExternalIntegration,
  CreateExternalIntegrationParams,
  loadExternalIntegrations,
} from 'dashboard/redux/thunk-actions/external-integrations';
import DashboardExternalIntegrationShowView from 'dashboard/views/DashboardExternalIntegrationsShowView/DashboardExternalIntegrationsShowView';
import {
  LEGACY_EXTERNAL_INTEGRATION_VENDORS,
  LegacyVendorName,
} from 'lib/constants/externalIntegrationVendors';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import { isPermissionsError } from 'lib/permissions-errors';

type Params = { externalIntegration: string };

const DashboardExternalIntegrationRoute = (props: PropsFromRedux) => {
  const { externalIntegration } = useParams<Params>() as Params; // externalIntegration will always be defined
  const { externalIntegrations, organization, currentEnvironment } = props;
  const navigate = useNavigate();

  const externalIntegrationVendor = LEGACY_EXTERNAL_INTEGRATION_VENDORS.find(
    (vendor) => vendor.name === (externalIntegration as LegacyVendorName),
  );

  const externalIntegrationType = externalIntegrationVendor?.[
    'model'
  ] as ExternalIntegration['type'];

  if (externalIntegrationType == null) {
    navigate('/dashboard/marketplace', { replace: true });
  }

  const filteredExternalIntegrations = externalIntegrations.filter(
    (externalIntegration) => externalIntegration.type === externalIntegrationType,
  );

  const { loading: isExternalIntegrationsLoading } = useApiLoader({
    loader: async () => {
      await props.loadExternalIntegrations();
    },
    onError: (err) => {
      if (isPermissionsError(err)) {
        props.clearExternalIntegrations();
      } else {
        props.showMessage('Failed to load connections, please try again', MessageType.Failure);
      }
      navigate('/dashboard/marketplace', { replace: true });
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
    deps: [props.loadExternalIntegrations, props.showMessage],
  });

  const { loading: isArchiveLoading, callback: archiveExternalIntegration } = useApiCallback(
    async (externalIntegration: ExternalIntegration) => {
      await props.archiveExternalIntegration(externalIntegration);
    },
    [props.archiveExternalIntegration],
  );

  const {
    loading: isCreateLoading,
    error: handleCreateExternalIntegrationError,
    callback: createExternalIntegration,
  } = useApiCallback(
    async (
      externalIntegrationType: ExternalIntegration['type'],
      integrationParams: CreateExternalIntegrationParams,
    ) => {
      await props.createExternalIntegration(externalIntegrationType, integrationParams);
    },
    [props.createExternalIntegration],
  );
  useApiErrorHandler({
    error: handleCreateExternalIntegrationError,
    onError: () => {
      props.showMessage('Failed to create connection', MessageType.Failure);
    },
    onNetworkError: () => {
      props.showMessage(
        'We are experiencing network issues. Please reload to try again.',
        MessageType.Failure,
      );
    },
  });

  const integrationsEnabled = !productFeatureDisabled(
    organization,
    ProductFeatureName.WorkflowsEnhanced,
    currentEnvironment?.type,
  );

  if (isExternalIntegrationsLoading) {
    return (
      <Backdrop>
        <LoadingIndicator fullscreen={true} />
      </Backdrop>
    );
  } else {
    return (
      <DashboardExternalIntegrationShowView
        externalIntegrations={filteredExternalIntegrations}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        externalIntegrationVendor={externalIntegrationVendor}
        archiveExternalIntegration={archiveExternalIntegration}
        createExternalIntegration={createExternalIntegration}
        isLoading={isArchiveLoading || isCreateLoading}
        integrationsEnabled={integrationsEnabled}
      />
    );
  }
};

const mapStateToProps = (state: RootState) => {
  return {
    organization: state.organization.organization,
    currentEnvironment: state.currentEnvironment.currentEnvironment,
    externalIntegrations: state.externalIntegrations.externalIntegrations,
  };
};

const mapDispatchToProps = {
  createExternalIntegration,
  loadExternalIntegrations,
  archiveExternalIntegration,
  clearExternalIntegrations,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardExternalIntegrationRoute);
